"use client";
import { useEffect, useState } from "react";
import { useAppContext } from "../app-context/provider";
import { AddToCartBtn } from "../components/add-to-cart-button";
import { ImageSlider } from "../components/image-gallery";
import { Product as CProduct } from "../components/item/product";
import type { Product as ProductType } from "~/use-cases/contracts/Product";
import { Price } from "../components/price";
import { ProductBody } from "../components/product/product-body";
import { TopicLabels } from "../components/product/topic-labels";
import { VariantSelector } from "../components/variant-selector";
import { ProductVariant } from "~/use-cases/contracts/ProductVariant";
import { buildSchemaMarkup } from "~/use-cases/SchemaMarkupBuilder";

import LogoVisa from "~/assets/logo-visa.svg";
import LogoMastercard from "~/assets/logo-mc.svg";
import LogoVipps from "~/assets/logo-vipps.svg";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import LipScoreReview from "../components/aplia/Lipscore/LipScoreReview";
import LipScoreReviewStars from "../components/aplia/Lipscore/LipScoreReviewStars";
import { useKlaviyo } from "../components/aplia/Klaviyo";

const Product = ({
  data,
  baseUrl,
}: {
  data: {
    product: ProductType;
    preSelectedSku: string;
  };
  baseUrl: string;
}) => {
  const router = useRouter();
  const pathName = usePathname();
  const { _t, state } = useAppContext();
  const { product, preSelectedSku } = data;
  const [showMore, setShowMore] = useState(false);
  const { trackClientEvent, isLoaded } = useKlaviyo();

  const toggleShowMore = () => {
    if (showMore) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    setShowMore((prevState) => !prevState);
  };
  const selectedVariant =
    product.variants.find((variant) => variant.sku === preSelectedSku) ??
    product.defaultVariant;
  const description = selectedVariant?.description || product.description;

  // Track product view when page loads or variant changes
  useEffect(() => {
    if (isLoaded && selectedVariant) {
      const price = selectedVariant.priceVariants?.default?.value || 0;
      trackClientEvent("Viewed Product", {
        product_id: selectedVariant.sku,
        product_name: product.title,
        sku: selectedVariant.sku,
        price: price,
        product_url: typeof window !== "undefined" ? window.location.href : "",
        product_image_url: selectedVariant.images?.[0]?.url || "",
      });

      window.dataLayer?.push({
        event: 'view_item',
        ecommerce: {
          currency: 'NOK',
          value: price, 
          items: [{
            item_id: selectedVariant.sku,
            item_name: product.title,
            price: price,
            item_category: product.topics?.[0]?.name || ''
          }]
        }
      });

      console.log("Product view tracked - dataLayer event pushed", {
        item_id: selectedVariant.sku,
        item_name: product.title,
        price: price,
      });
    }
  }, [isLoaded, selectedVariant?.sku, product.title]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            buildSchemaMarkup(product, selectedVariant, baseUrl, state),
          ),
        }}
      />
      <div className="productPage">
        <div className="px-4 lg:px-6 mx-auto xl:container full">
          <div className="flex gap-1 md:gap-6 lg:gap-14 xl:gap-28 md:flex-row flex-col-reverse">
            <div className="lg:w-7/12 w-full">
              <div className="img-container overflow-hidden hidden md:block">
                {/* Images on desktop */}
                <ImageSlider images={selectedVariant?.images} />
              </div>
              <div
                className="product-description"
                style={{
                  maxHeight: showMore ? "100%" : "500px",
                  marginBottom: showMore ? "20px" : "60px",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease",
                }}
              >
                <ProductBody
                  story={product?.story}
                  propertiesTable={product?.specifications}
                  dimensions={product?.dimensions}
                  downloads={product?.downloads}
                />
                <div
                  className={`read-more-wrapper ${!showMore ? "shaded" : ""}`}
                >
                  <button className="read-more-toggle" onClick={toggleShowMore}>
                    {!showMore ? <span>Vis mer</span> : <span>Vis mindre</span>}
                  </button>
                </div>
              </div>
            </div>
            <div className="lg:w-5/12 xl:w-4/12 w-full">
              <div className="flex flex-col gap-2 md:sticky top-28 pb-0 md:pb-10">
                <div className="mb-2 hidden md:block">
                  {product.topics && <TopicLabels topics={product.topics} />}
                </div>
                <div className="img-container overflow-hidden block md:hidden mx-auto w-full">
                  {/* Images on mobile */}
                  <ImageSlider images={selectedVariant?.images} />
                </div>
                <div className="pr-6 mb-6">
                  <h1 className="font-semibold text-xl md:text-3xl lg:text-4xl mb-2">
                    {product.title}
                  </h1>
                  <LipScoreReviewStars
                    product={product}
                    selectedVariant={selectedVariant}
                  />
                  <p className="text-md font-normal">{description}</p>
                </div>
                <VariantSelector
                  variants={product.variants}
                  selectedVariant={selectedVariant}
                  onVariantChange={(variant: ProductVariant) => {
                    router.push(`${pathName}?sku=${variant.sku}`);
                  }}
                  renderingType="default"
                />
                {selectedVariant && (
                  <div className="">
                    <Price variant={selectedVariant} />
                    {selectedVariant.outofstock.content?.value === true ? (
                      <div className="pt-10 text-xl text-red-900">
                        Tomt på lager
                      </div>
                    ) : (
                      <AddToCartBtn
                        pack={[
                          {
                            variant: selectedVariant,
                            quantity: 1,
                            dimensions: product.dimensions,
                          },
                        ]}
                      />
                    )}
                  </div>
                )}
                <div className="payment-methods pt-8 md:pt-16 mb-4 sm:hidden">
                  <div className="payment-methods__wrapper">
                    <div>
                      <Image src={LogoVisa} alt="" />
                    </div>
                    <div>
                      <Image src={LogoMastercard} alt="" />
                    </div>
                    <div>
                      <Image src={LogoVipps} alt="" />
                    </div>
                    <div>
                      <Image
                        width={200}
                        height={200}
                        className="min-w-[70px]!"
                        src="/qliro_black.png"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                {product.ean && (
                  <div className="pr-6">
                    <strong>Ean:</strong> {product.ean}
                  </div>
                )}
                <div className="mb-2 mt-5 block md:hidden">
                  {product.topics && <TopicLabels topics={product.topics} />}
                </div>
                <LipScoreReview
                  product={product}
                  selectedVariant={selectedVariant}
                />
              </div>
            </div>
          </div>

          {product.relatedItems.length > 0 && (
            <div className="w-full border-t border-[#dfdfdf] pr-6 sm:pr-0 pb-10">
              <h2 className="font-bold mt-10 mb-8 text-2xl">
                {_t("relatedProducts")}
              </h2>
              <div className="grid gap-5 grid-cols-2 md:grid-cols-4 lg:grid-cols-5 pb-5">
                {product.relatedItems.map((item, index) => (
                  <CProduct item={item} key={`${item.path}-${index}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Product;
